// ---------------------------------------------------------------------------
// One finished game, at /game/:gameId.
//
// Public, and the same privacy rule as the profile applies: render exactly
// `GameRecordResponse` and add nothing to it. The endpoint is built to the same
// rule, so between the two of them there is no field here that could carry an
// email address, a Clerk id, a device UUID, or the room's code.
//
// It is a record rather than a replay. Nothing here is paced, nothing is
// released a beat at a time, and there is no clock: the whole game is on the
// page, and the reader scrolls. DESIGN section 15 still lists replay playback as
// deferred and this does not do it.
//
// The pieces come from `components/round/`, which is where the reveal's frames,
// tile rows and working now live. What this screen does NOT do is build a fake
// `ClientGameState` so it can mount `Stage` wholesale. Four of that component's
// inputs — who was locked out, who buzzed, who solved, and an AI's reasoning —
// are not persisted at all, and the adapter would have to invent them. A record
// that invents things is worse than a smaller record.
//
// `seatAsPlayer` below is the one adaptation, and it is deliberately small
// enough to read in full: every field it fills is either stored or is a live
// room fact with no meaning for a finished game.
//
// --- A round is a table, and it used to be a stack of cards ------------------
//
// **Every answer was a 171px `<section>` and there are as many of them as there
// are players times rounds.** An ordinary game — eight players, five rounds —
// came to 9,606px, of which 6,800 was forty of those cards: a name, a model id,
// `claimed 5`, the word in a 46px tile row, `5 letters`, and a score, stacked
// vertically, per player, per round. Nothing there was wrong; there was simply a
// card around every row of a table.
//
// So a round is a real `<table>` now — `<th scope="col">` over the columns,
// `<th scope="row">` on the player, one row per answer — for the same reason
// `components/charts/Histogram.tsx` is one: a grid of divs is a regression for
// anybody not looking at it.
//
// Measured on that same game at 375 by 1079: 9,606px became 3,432. A letters
// round went from 1,760 to between 485 and 559 — the spread is how many answers
// were ruled out, since only those rows carry a second line — the numbers round
// from 2,976 to 742, and the conundrum from 427 to 236. A row is 34px, and 48
// when its reason chip drops to a second line on a phone. On a desktop every row
// is 34.
//
// Three facts were CUT rather than shrunk, and each of them was the page saying
// something twice:
//
//   - `n letters` under a good word. The word is spelled out two columns to the
//     left and the score is one column to the right; the reveal dropped the same
//     line for the same reason (see the note above `reasonForSubmission`).
//   - `claimed n` on every answer. It is only a second fact when it DISAGREES
//     with what was played — otherwise it is the word's own length, counted
//     again — so it is drawn only then.
//   - The `@handle` / `guest` / `signed in` slot on the per-round rows, via
//     `showIdentity={false}`. It is unchanged in the standing at the top of the
//     page, which is where a reader looks up who somebody is. Forty repeats of
//     it inside the rounds were taking the width the word needed.
//
// Everything else was made smaller, not hidden: the word is mono text rather
// than a tile row, the numbers working is its own compact line rather than a
// wrapped strip of chips, and the best answer is the table's `<tfoot>` — which
// is not only shorter than the blue frame it replaces but puts what was there in
// the same column as what everybody played. Nothing is behind a disclosure.
//
// The round is drawn in a plain section rather than in `Card`, purely for its
// padding: `Card` is `p-5` at every width, and on a 375px phone those sixteen
// horizontal pixels are the difference between the working fitting on two lines
// and on three. It is the same surface on the same background.
//
// --- A conundrum has more on it than the buzz -------------------------------
//
// A conundrum round also draws `What the AI had`, which is the reveal's own
// block: every AI seat's answer, whether or not it ever got to say it. The
// round takes one buzz, so drawing only the submissions drew one seat and made
// a round eight other models had solved look like a round nobody else was in.
// See `ShadowAnswers`. It is fed by `shadowAnswers` on the response, which
// `db/gameDetail.ts` fills and comments the exposure rule for.
//
// --- What a word means -------------------------------------------------------
//
// Every word this page draws that the ROUND put on the screen can be asked
// about, and the answer floats beside the word rather than landing in a line
// under the round. **That reverses what shipped**, and the reason is on
// `WordDefinition.tsx`: a line in the flow moved the table's neighbours down
// every time a pointer crossed a word. The ways in and the placement rule are
// both over there; the only decision taken here is which words are askable, and
// it is taken by mirroring the server's own membership set rather than by taste.
//
// **This page keeps the inset-shadow mark under an askable word**, which the
// reveal has dropped. A record is a dense table where the words are the content
// and nothing else on it says they can be pressed; the reveal is a handful of
// big words on a stage, where the same rule read as clutter. See `AskableWord`.
//
// `revealedWordsOfRecordRound` in `packages/backend/src/definitions.ts` admits
// every submission's answer, the conundrum's solution, the AI shadow answers,
// and the best answer with its alternatives. So the played word, the AI rows and
// the best row are controls, and nothing else on the page is.
//
// **`What the AI had` was NOT askable until the contract admitted it**, and that
// was a gap in the contract rather than a choice about the page: a live
// conundrum round has admitted its shadow answers since the day it had them, and
// the stored one did not, so this page printed words in full that it would have
// refused to define. It reads worse the more the page shows — a room seating
// nine models prints nine words here and could define one of them. The stored
// round now admits them exactly as the live one does.
//
// One consequence worth knowing before moving anything: the pick is per ROUND,
// and a round has two places a word can be pressed. That used to decide where
// the line landed — under the block it was asked from, or a press in the AI
// block answered somewhere above it — and the popup has taken that job away,
// since it is drawn beside the word wherever the word is. `picked` still carries
// where it came from, for the reason that outlived the other: two identical
// words, one played and one only thought of, are distinguishable, which they
// would not be if the state were a bare string.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AGGREGATE_ROWS } from '@countdown/shared';
import type {
  AggregateEntry,
  BestAnswer,
  GameRecordPlayerView,
  GameRecordResponse,
  GameRecordRoundView,
  GameRecordSubmissionView,
  NumbersStep,
  PublicPlayer,
  RoundAggregate,
  Team,
} from '@countdown/shared';

import { AppHeader } from '../components/layout/AppHeader.js';
import { PlayerChip } from '../components/primitives/PlayerChip.js';
import { RoundTypeChip } from '../components/primitives/RoundTypeChip.js';
import { NumberTile } from '../components/primitives/NumberTile.js';
import { Breakdown, rankBlocks, sortBlocks } from '../components/round/Breakdown.js';
import type { BreakdownBlock, BreakdownRound } from '../components/round/Breakdown.js';
import { TileRow, stepText } from '../components/round/atoms.js';
import { DefinitionPopover, DefinitionWord } from '../components/round/WordDefinition.js';
import { useWordDefinition } from '../hooks/useWordDefinition.js';
import { getGameRecord } from '../lib/api.js';
import { listNames, modelPath, roundLabel } from '../lib/format.js';
import { RULED_OUT_LABEL, ruledOutKind } from '../lib/ruling.js';
import type { RuledOutKind } from '../lib/ruling.js';
import {
  aggregateRecordRound,
  recordFindersLine,
  recordIsAggregated,
} from './gameRecordAggregate.js';
// The four pure readers of a `RoundAggregate`, taken from the live reveal rather
// than written again here. Two of them are WORDING — `n other answers, from m
// players`, `n played nothing` — and a second copy of a sentence is a sentence
// that drifts, so the permanent page and the live screen say the same thing by
// construction. The other two are three lines each and come along for the ride
// rather than being split across two homes. Nothing in that module is touched
// from here; it is imported and read.
import {
  noAnswerLine,
  otherAnswersLine,
  playersBehind,
  splitByRuling,
} from './room/reveal/aggregate.js';

type Load =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; record: GameRecordResponse };

function isMissing(error: Error): boolean {
  return /no such game/i.test(error.message) || /\(404\)/.test(error.message);
}

function longDate(at: number): string {
  return new Date(at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// --- Adapting a stored seat --------------------------------------------------

/**
 * A stored seat as `PlayerChip` and the round-by-round table want it.
 *
 * Every field that describes the player is stored. The four that are not —
 * `connected`, `muted`, `waitingForNextGame`, `isHost` — are facts about a live
 * room, and their values here mean "this is not a live room" rather than a claim
 * about what happened: a finished seat is not disconnected, is not muted, is not
 * waiting, and hosts nothing.
 *
 * `registered` is the one honest limitation. The record stores a username and
 * not the flag, so an account that signed in and never finished picking a
 * username is drawn with the guest tag. That is what the stored row supports;
 * inventing the flag from nothing would be worse.
 *
 * **A null colour is passed through as null**, which it did not used to be: it
 * fell back to `var(--color-ink-dim)`, and that is the substitution
 * `colorForSeat` exists to forbid. Past `COLORED_SEATS` nobody in the room has a
 * colour — all of them, not the ones past sixteen — because a grey is itself a
 * colour and reads as an identity, and a five-hundred-seat game is told apart by
 * name. The seat also stores null for a game recorded before the column existed,
 * and drawing that as a colour was a claim the row could not support either.
 */
function seatAsPlayer(seat: GameRecordPlayerView): PublicPlayer {
  return {
    id: seat.playerId,
    username: seat.username,
    registered: seat.username !== null,
    displayName: seat.displayName,
    color: seat.color,
    isBot: seat.isBot,
    botKind: seat.botKind,
    botDifficulty: seat.botDifficulty,
    botModel: seat.botModel,
    botReasoning: false,
    role: 'player',
    // A finished seat stood down from nothing. `leftSeat` is a fact about a
    // game still being played — somebody watching the rest of one at the score
    // they had — and a stored seat is in the standing on its own terms.
    leftSeat: false,
    teamId: seat.teamId,
    score: seat.finalScore,
    connected: true,
    muted: false,
    waitingForNextGame: false,
    isHost: false,
  };
}

/**
 * The colour a seat is drawn in: the team's while teams were on, else its own.
 *
 * **Null, not undefined, when there is none.** `PlayerChip` reads the two apart
 * — undefined is a caller with no state to hand and falls back to the player's
 * own colour, null is a room that has no colours in it and paints no dot — and
 * this caller always knows which it has. Past `COLORED_SEATS` every stored seat
 * is null, and so is every seat of a game recorded before the column existed.
 */
function colorOf(seat: GameRecordPlayerView, teams: Team[]): string | null {
  if (seat.teamId === null) return seat.color;
  return teams.find((team) => team.id === seat.teamId)?.color ?? seat.color;
}

function rejected(answer: GameRecordSubmissionView): boolean {
  if (answer.hostOverride === 'reject') return true;
  if (answer.hostOverride === 'allow') return false;
  return answer.valid === false;
}

function roundsForTable(record: GameRecordResponse): BreakdownRound[] {
  return record.rounds.map((round) => {
    const cells: BreakdownRound['cells'] = {};
    for (const answer of round.submissions) {
      cells[answer.playerId] = { score: answer.score ?? 0, rejected: rejected(answer) };
    }
    return { index: round.index, type: round.type, cells };
  });
}

/**
 * The standing, grouped by team when the game had them.
 *
 * Two differences from the room's builder, both because a record is not a room.
 *
 * A team's total is recomputed as the sum of its best answers, because
 * `state.teamScores` does not survive into a record — that is the definition of
 * the number rather than a second source for it.
 *
 * A solo seat keeps its STORED placement rather than one derived from the
 * totals. The stored one is the engine's own answer and it is what the
 * leaderboard counts, so deriving a second one here would let this page and the
 * profile linking to it disagree about who won. Teams have no stored placement,
 * so those are still derived.
 */
function blocksForTable(record: GameRecordResponse): BreakdownBlock[] {
  const rounds = roundsForTable(record);
  const scoreOf = (round: BreakdownRound, playerId: string) => round.cells[playerId]?.score ?? 0;
  const blocks: BreakdownBlock[] = [];
  const teamed = record.teams.length > 0;

  if (teamed) {
    for (const team of record.teams) {
      const members = record.players.filter((seat) => seat.teamId === team.id);
      if (members.length === 0) continue;
      const perRound = rounds.map((round) =>
        members.reduce((best, seat) => Math.max(best, scoreOf(round, seat.playerId)), 0),
      );
      blocks.push({
        key: team.id,
        team,
        members: members.map(seatAsPlayer),
        perRound,
        total: perRound.reduce((sum, score) => sum + score, 0),
        placement: 1,
      });
    }
  }

  for (const seat of record.players) {
    if (teamed && seat.teamId !== null) continue;
    blocks.push({
      key: seat.playerId,
      team: null,
      members: [seatAsPlayer(seat)],
      perRound: rounds.map((round) => scoreOf(round, seat.playerId)),
      total: seat.finalScore,
      placement: seat.placement,
    });
  }

  // Teams need theirs worked out; solo seats brought their own.
  return teamed ? rankBlocks(blocks) : sortBlocks(blocks);
}

// --- Screen ------------------------------------------------------------------

export function GameRecordScreen() {
  const { gameId } = useParams<{ gameId: string }>();
  /** Tagged with the id it answers, so a stale reply cannot be shown. */
  const [answer, setAnswer] = useState<{ gameId: string; load: Load } | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let live = true;

    getGameRecord(gameId)
      .then((record) => {
        if (live) setAnswer({ gameId, load: { status: 'ready', record } });
      })
      .catch((error: Error) => {
        if (!live) return;
        setAnswer({
          gameId,
          load: isMissing(error)
            ? { status: 'missing' }
            : { status: 'failed', message: error.message },
        });
      });

    return () => {
      live = false;
    };
  }, [gameId]);

  const load: Load = !gameId
    ? { status: 'missing' }
    : answer?.gameId === gameId
      ? answer.load
      : { status: 'loading' };

  return (
    <>
      <AppHeader />
      <main className="mx-auto w-full max-w-[860px] px-4 py-6 sm:px-6">
        {load.status === 'loading' && (
          <p className="py-16 text-center text-ink-dim" aria-live="polite">
            Loading this game.
          </p>
        )}

        {load.status === 'missing' && (
          <div className="py-16 text-center">
            <h1 className="text-2xl font-bold">No game by that link</h1>
            <p className="mx-auto mt-3 max-w-[380px] text-ink-muted text-pretty">
              Games are recorded once they reach the end, so a game that was
              abandoned partway has nothing to show.
            </p>
            <Link to="/" className="mt-6 inline-block min-h-11 leading-11 text-blue-200 underline">
              Back to the home page
            </Link>
          </div>
        )}

        {load.status === 'failed' && (
          <div className="py-16 text-center">
            <h1 className="text-2xl font-bold">That game did not load</h1>
            <p className="mt-3 text-ink-muted">{load.message}</p>
            <Link to="/" className="mt-6 inline-block min-h-11 leading-11 text-blue-200 underline">
              Back to the home page
            </Link>
          </div>
        )}

        {load.status === 'ready' && <GameRecord record={load.record} />}
      </main>
    </>
  );
}

/**
 * The record itself, without the fetch around it.
 *
 * Exported so the component gallery can mount one from a fixture. Every screen
 * in the gallery is a pure function of state; this one would otherwise be the
 * exception that needs a server.
 */
export function GameRecord({ record }: { record: GameRecordResponse }) {
  const seatById = new Map(record.players.map((seat) => [seat.playerId, seat]));

  // The round-by-round table is one row per player and one cell per round, so a
  // five-hundred-seat, fifteen-round game is 7,500 cells — the exact shape the
  // aggregated round below exists to avoid, printed once at the top of the page.
  // So above the line it is the top of the standing and a count of the rest,
  // capped at `AGGREGATE_ROWS` because that is already this feature's answer to
  // "how many rows can be read at once". The full standing is not lost: every
  // seat is still in the response, and every ROUND still accounts for all of
  // them.
  const blocks = blocksForTable(record);
  const shown = recordIsAggregated(record) ? blocks.slice(0, AGGREGATE_ROWS) : blocks;
  const hidden = blocks.length - shown.length;

  return (
    // `gap-3` rather than `gap-5`: every block below is a bordered surface, so
    // the edge is doing the separating and the extra eight pixels between eight
    // blocks were only length.
    <div className="flex flex-col gap-3">
      <Header record={record} />

      <Breakdown rounds={roundsForTable(record)} blocks={shown} />
      {hidden > 0 ? (
        <p data-standing-rest="" className="font-mono text-[11px] text-ink-dim">
          and {hidden} more {hidden === 1 ? 'player' : 'players'}, further down the standing
        </p>
      ) : null}

      <ModelsInGame record={record} />

      {record.rounds.map((round) => (
        <Round key={round.index} record={record} round={round} seatById={seatById} />
      ))}
    </div>
  );
}

function Header({ record }: { record: GameRecordResponse }) {
  // Winners come from the standing rather than from `outcome.winnerIds`: those
  // are score-bucket ids, so a team id and a player id are indistinguishable and
  // a three-person team's win would render as a three-way tie.
  const top = record.players.filter((seat) => seat.placement === 1);
  const teamed = record.teams.length > 0;
  const winnerNames = teamed
    ? [
        ...new Set(
          top.map(
            (seat) =>
              record.teams.find((team) => team.id === seat.teamId)?.name ?? seat.displayName,
          ),
        ),
      ]
    : top.map((seat) => seat.displayName);

  // The date used to sit on a line of its own above the headline and the shape
  // of the game on a third below it. They are one sentence about when and what,
  // so they are one line, under the only thing here worth a heading.
  return (
    <header className="flex flex-col gap-1">
      <h1 className="text-[28px] leading-tight font-bold text-pretty">
        {winnerNames.length === 0
          ? 'An unfinished game'
          : winnerNames.length === 1
            ? `${winnerNames[0]} won`
            : `${listNames(winnerNames)} shared it`}
      </h1>
      <p className="text-[13px] text-ink-dim">
        {longDate(record.startedAt)} &middot; {record.players.length}{' '}
        {record.players.length === 1 ? 'player' : 'players'}, {record.rounds.length}{' '}
        {record.rounds.length === 1 ? 'round' : 'rounds'}
        {record.outcome.reason === 'ended-early' ? ', ended early' : ''}
        {record.outcome.reason === 'abandoned' ? ', abandoned' : ''}
      </p>

      {/* **The page exists and every answer on it is real; what is not real is
          any comparison with another game.** A host with rulings on could allow
          a word or strike one, so this game is in no leaderboard, no total and
          no distribution — and a page that looked exactly like every other page
          would be inviting exactly the comparison it cannot support.

          Said here, once, under the headline. Not on each answer: a ruling
          landing on one word does not make the other forty untrustworthy, and
          forty markers would say the opposite. */}
      {record.ranked ? null : (
        <p
          data-testid="unranked-record"
          className="mt-1 rounded-chip bg-accent/15 px-2.5 py-2 text-[12px] text-accent"
        >
          The host could allow or reject words in this game, so it counts towards
          nobody’s statistics.
        </p>
      )}
    </header>
  );
}

/**
 * A line into the AI opponents' own pages, for the games that had one.
 *
 * Here rather than on the chips inside the table: `PlayerChip` is drawn in the
 * live room, in the reveal and in the chat byline, and giving it a route would
 * make a primitive depend on a router that half its callers — the component
 * gallery among them — do not mount.
 *
 * Distinct by model, not by seat. Two seats held by one model are two players in
 * the standing above and one link here, because there is one page behind it.
 */
function ModelsInGame({ record }: { record: GameRecordResponse }) {
  const seen = new Map<string, string>();
  for (const seat of record.players) {
    if (seat.botModel !== null && !seen.has(seat.botModel)) {
      seen.set(seat.botModel, seat.displayName);
    }
  }
  if (seen.size === 0) return null;

  return (
    <p className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-ink-dim">
      <span className="label-mono">ai opponents</span>
      {[...seen.keys()].map((modelId) => {
        const path = modelPath(modelId);
        const name = modelId.split('/').pop() ?? modelId;
        return path === null ? (
          <span key={modelId} className="font-mono text-[11px]">
            {name}
          </span>
        ) : (
          <Link key={modelId} to={path} className="font-mono text-[11px] text-blue-200 underline">
            {name}
          </Link>
        );
      })}
    </p>
  );
}


// --- One round ---------------------------------------------------------------

/**
 * The column widths, stated because the table is `table-fixed`.
 *
 * Fixed rather than automatic for one reason: `PlayerChip` truncates its name
 * against the width it is given, and an automatic table gives a column whatever
 * its longest cell asks for. One long display name would then take the width the
 * words need in every row of the round, and a round whose names happen to be
 * short would lay out differently from the round above it.
 *
 * **The name gets slightly less room in a numbers round**, because what sits
 * beside it is longer: a word is 81px at its worst — nine characters of 15px
 * mono — and numbers working is thirty-odd characters that wrap onto a second
 * line and could wrap onto a third.
 *
 * Both numbers were found by measuring rather than reasoned to, and the
 * measurement is worth writing down because the obvious guess is wrong. Numbers
 * was 38% first, on the theory that the working needed every pixel; it cost four
 * characters of every display name and bought nothing at all — the round came out
 * at exactly the same height at 44%, because what the working actually wraps at
 * is the end of a step, not the end of the column. Do not narrow it again
 * without measuring the round.
 *
 * The score column is the only one in pixels: it holds at most three digits and
 * a percentage of a 375px phone is not enough for them.
 */
const PLAYER_COLUMN_WORDS = '48%';
const PLAYER_COLUMN_NUMBERS = '44%';
const SCORE_COLUMN_PX = 38;

/** The board's letters, or an empty list for a round that stored none. */
function lettersOf(round: GameRecordRoundView): string[] {
  return (round.letters ?? []).map((tile) => tile.letter);
}

/**
 * What the chip beside a word says, or null when there is nothing to say.
 *
 * A host ruling is reported in BOTH directions, which is why this is not simply
 * "the reason it was rejected": what the chip reports is that a person decided
 * this, not that the score moved. That matches the reveal, and it is the only
 * branch here that can fire on a word that still stands.
 *
 * In practice no recorded game has one — a game played with host rulings on is
 * not written to the database at all (`gameIsRecorded`) — but the field is on
 * the response and a page that renders a field it cannot explain is worse than
 * one branch that rarely runs.
 */
function reasonOf(
  round: GameRecordRoundView,
  answer: GameRecordSubmissionView,
): RuledOutKind | null {
  if (answer.hostOverride !== null) return 'host-ruled';
  if (!rejected(answer)) return null;
  return ruledOutKind({
    type: round.type,
    word: answer.answer ?? '',
    tiles: lettersOf(round),
    hostRuled: false,
  });
}

/**
 * A word being asked about, which of a round's two blocks it was asked from, and
 * the button it was pressed on.
 *
 * `from` is not decoration, and it outlived the reason it was added. It used to
 * decide WHERE the answer landed — a round draws its answer table and, for a
 * conundrum, `What the AI had` beneath it, and each block took its own line
 * directly under itself, so a press in the AI block that answered above the AI
 * block would have been pointing at the wrong list. The answer floats beside the
 * word now, so it cannot land under the wrong block at all. What `from` still
 * does is the other thing it always did: it tells two identical words apart. A
 * model that worked out the word somebody else played appears in both blocks,
 * and a bare string would light both of them up.
 *
 * `anchor` is the DOM node the answer is placed against. Held rather than looked
 * up, because a `[data-definition-word]` query would find the FIRST of those two
 * identical words and the pair is exactly what `from` exists for.
 */
interface PickedWord {
  word: string;
  from: 'answers' | 'shadows';
  anchor: HTMLElement;
}

function Round({
  record,
  round,
  seatById,
}: {
  record: GameRecordResponse;
  round: GameRecordRoundView;
  seatById: Map<string, GameRecordPlayerView>;
}) {
  const chooser = round.chooserPlayerId ? seatById.get(round.chooserPlayerId) : undefined;

  /**
   * The word this round is being asked about, per round rather than per page.
   *
   * A round owns its own line, so reading one round's answer does not blank the
   * line of a round somebody scrolled past. `round.index` is the round's own
   * index and is what the endpoint takes — not the position in `record.rounds`,
   * which is the same number today and is not the thing being named.
   *
   * One answer per round still, and one place it is drawn — beside the word,
   * wherever in the round that word is. The two blocks are mutually exclusive by
   * construction rather than by care, because there is one piece of state and it
   * names where it came from. See `PickedWord`.
   */
  const [picked, setPicked] = useState<PickedWord | null>(null);
  const definition = useWordDefinition(
    picked === null ? null : { gameId: record.gameId, roundIndex: round.index, word: picked.word },
  );

  // Best answer last, which is the order the reveal releases them in and the
  // order that makes a round readable: what everybody had, then what was there.
  // The table's `<tfoot>` is what was there, so the climb still lands on it.
  const answers = [...round.submissions].sort(
    (a, b) => (a.score ?? 0) - (b.score ?? 0) || (a.declaredLength ?? 0) - (b.declaredLength ?? 0),
  );

  // Null for every ordinary game, so the page below is exactly what it was.
  // Above `LARGE_ROOM_FROM` seats a round is one row per ANSWER instead of one
  // row per player — see `gameRecordAggregate.ts` for the threshold and for what
  // this may and may not change.
  const aggregate = recordIsAggregated(record)
    ? aggregateRecordRound(round, record.players.length)
    : null;

  return (
    // Not `Card`, only because `Card` is `p-5` at every width. See the note at
    // the top of the file: those sixteen horizontal pixels are a line of numbers
    // working on a phone.
    <section
      className="rounded-frame border border-line bg-surface px-3 py-4 sm:px-5 sm:py-5"
      data-round-index={round.index}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <RoundTypeChip type={round.type} size="sm" />
        <h2 className="text-[17px] leading-none font-bold">
          Round {round.index + 1}: {roundLabel(round.type)}
        </h2>
        {chooser ? (
          <span className="ml-auto text-[11px] text-ink-dim">{chooser.displayName} chose</span>
        ) : null}
      </div>

      <div className="mt-3 flex flex-col gap-3">
        <Board round={round} />
        {aggregate === null ? (
          <AnswerTable
            record={record}
            round={round}
            answers={answers}
            seatById={seatById}
            picked={picked?.from === 'answers' ? picked.word : null}
            onPick={(word, anchor) => setPicked({ word, from: 'answers', anchor })}
          />
        ) : (
          <AggregateTable
            round={round}
            aggregate={aggregate}
            seatById={seatById}
            picked={picked?.from === 'answers' ? picked.word : null}
            onPick={(word, anchor) => setPicked({ word, from: 'answers', anchor })}
          />
        )}
        <ShadowAnswers
          record={record}
          round={round}
          seatById={seatById}
          picked={picked?.from === 'shadows' ? picked.word : null}
          onPick={(word, anchor) => setPicked({ word, from: 'shadows', anchor })}
        />
        {/* One panel for the round, drawn beside whichever word was pressed —
            which is why there is no longer one of these per block. It portals to
            the body and occupies nothing here, so a press cannot move the table
            it was made from. Nothing is drawn while the request is in flight;
            once it lands there is always something, including for the two words
            in three with no gloss. See `WordDefinition.tsx`. */}
        {picked === null ? null : (
          <DefinitionPopover word={picked.word} anchor={picked.anchor} answer={definition} />
        )}
      </div>
    </section>
  );
}

/**
 * What the AI opponents had, for a conundrum only one of them could say.
 *
 * **The record page showed the seat that buzzed and nothing else.** A conundrum
 * takes one buzz — the engine refuses the second buzzer outright — so a round
 * with nine models in it finishes with eight answers that were computed, paid
 * for, and never said. The reveal has drawn them since the day they were kept;
 * the permanent page a link points at drew none of them, which reads as though
 * one model had been the only one thinking.
 *
 * It is the reveal's block, not a new one: the same heading, the same count
 * line, the same marks. Two of those marks are load-bearing and are the reason
 * this is not simply a list of words.
 *
 *   **`after the round`.** A model may take two minutes, and the answer is kept
 *   because the compute was bought. It is not evidence the model could have won,
 *   so it is never quietly listed beside the ones that beat the clock.
 *
 *   **`played it`.** Derived from the seat's own submission on this round rather
 *   than carried on the shadow. The database has the flag and the response
 *   deliberately does not: a second copy of a fact that is already on the page
 *   is a boolean that can disagree with the answer beside it.
 *
 * Nothing here scored, and nothing here says otherwise: no points column, and
 * the block sits under the answer table rather than inside it.
 */
function ShadowAnswers({
  record,
  round,
  seatById,
  picked,
  onPick,
}: {
  record: GameRecordResponse;
  round: GameRecordRoundView;
  seatById: Map<string, GameRecordPlayerView>;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  const shadows = round.shadowAnswers ?? [];
  if (shadows.length === 0) return null;

  const solved = shadows.filter((shadow) => shadow.correct).length;
  const buzzed = new Set(round.submissions.map((answer) => answer.playerId));

  return (
    <div
      data-ai-shadows="true"
      className="flex flex-col gap-1.5 rounded-card border border-line/60 bg-surface-2 px-3 py-2.5"
    >
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
        <h3 className="text-[15px] leading-none font-bold">What the AI had</h3>
        {/* The count says it once. A tick per row would say it n times. */}
        <span className="label-mono">
          {solved} of {shadows.length} right
        </span>
      </div>
      <p className="text-[13px] text-ink-dim">
        Only one buzz counts, so most of these were never played.
      </p>

      <ul aria-label="What each AI worked out" className="flex flex-col gap-1">
        {shadows.map((shadow) => {
          const seat = seatById.get(shadow.playerId);
          const answer = shadow.answer.toUpperCase();

          return (
            <li
              key={shadow.playerId}
              data-shadow-player={shadow.playerId}
              className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
            >
              <span
                className="min-w-0 shrink-0"
                style={{ width: PLAYER_COLUMN_WORDS }}
              >
                {seat === undefined ? (
                  <span className="text-[13px] text-ink-dim">A seat that left</span>
                ) : (
                  <PlayerChip
                    player={seatAsPlayer(seat)}
                    color={colorOf(seat, record.teams)}
                    size="sm"
                    showIdentity={false}
                  />
                )}
              </span>
              <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {/* An em dash is not a word and cannot be asked about, so a
                    model that answered nothing keeps a plain span rather than a
                    button that would 403. A WRONG answer stays askable, on the
                    same reasoning as a ruled-out word in the table above: `not a
                    fit` is this board's verdict, and whether a dictionary has
                    heard of it is the next question somebody asks. */}
                {answer.length === 0 ? (
                  <span className="font-mono text-[15px] font-bold tracking-[0.06em] text-ink-dim">
                    —
                  </span>
                ) : (
                  <DefinitionWord
                    word={answer}
                    picked={picked === answer}
                    onPick={(anchor) => onPick(answer, anchor)}
                    className={`font-mono text-[15px] font-bold tracking-[0.06em] ${
                      shadow.correct ? '' : 'text-ink-dim'
                    }`}
                  >
                    {answer}
                  </DefinitionWord>
                )}
                {shadow.correct ? null : <ShadowChip tone="alert">not a fit</ShadowChip>}
                {buzzed.has(shadow.playerId) ? <ShadowChip tone="dim">played it</ShadowChip> : null}
                {/* Kept, and never counted as a solve it could have had. */}
                {shadow.landedInTime ? null : (
                  <ShadowChip tone="dim">after the round</ShadowChip>
                )}
              </span>
              <span className="tabular ml-auto shrink-0 font-mono text-[11px] text-ink-dim">
                {(shadow.latencyMs / 1000).toFixed(1)}s
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A mark beside a shadow answer.
 *
 * Not `ReasonChip`: that one names a `RuledOutKind`, and none of these three is
 * a ruling — a shadow was never judged, never scored and never played. Same
 * skin, so the round reads as one thing.
 */
function ShadowChip({ tone, children }: { tone: 'alert' | 'dim'; children: string }) {
  return <RoundChip tone={tone}>{children}</RoundChip>;
}

/**
 * The chip skin, in its two washes, with nothing said about what it means.
 *
 * Three callers now say three different kinds of thing in it — a ruling
 * (`ReasonChip`), a fact about an AI's answer (`ShadowChip`), and a count of
 * rules on the aggregated round — so the skin is one place and the meaning is
 * the caller's. The alert wash is the same one `RulingBar` wears: one wash over
 * an opaque surface, never nested inside another.
 */
function RoundChip({
  tone,
  children,
  reason,
}: {
  tone: 'alert' | 'dim';
  children: string;
  reason?: RuledOutKind;
}) {
  return (
    <span
      className={`shrink-0 rounded-chip px-1.5 py-px font-mono text-[10px] font-semibold tracking-[0.06em] uppercase ${
        tone === 'alert' ? 'bg-alert/15 text-alert' : 'bg-raised text-ink-dim'
      }`}
      data-reason={reason}
    >
      {children}
    </span>
  );
}

/** The board, the six and the target, or the scramble. */
function Board({ round }: { round: GameRecordRoundView }) {
  if (round.type === 'letters' && round.letters) {
    return <TileRow letters={round.letters.map((tile) => tile.letter)} tone="board" />;
  }

  if (round.type === 'numbers' && round.numbersTiles) {
    // Six 48px tiles and five 4px gaps is 308, against the 319 a 375px phone
    // leaves inside this section — one line, which is what a board is.
    return (
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex flex-wrap justify-center gap-1">
          {round.numbersTiles.map((value, index) => (
            <NumberTile key={`${value}-${index}`} value={value} state="available" />
          ))}
        </div>
        <p className="tabular font-mono text-[26px] leading-none font-extrabold">
          {/* A bare number under a board is obviously the target to anybody
              looking at it and obviously nothing to anybody who is not. */}
          <span className="sr-only">target </span>
          {round.numbersTarget}
        </p>
      </div>
    );
  }

  if (round.type === 'conundrum' && round.conundrumScramble) {
    return <TileRow letters={[...round.conundrumScramble]} tone="board" />;
  }

  return null;
}

// --- The answers -------------------------------------------------------------

function AnswerTable({
  record,
  round,
  answers,
  seatById,
  picked,
  onPick,
}: {
  record: GameRecordResponse;
  round: GameRecordRoundView;
  answers: GameRecordSubmissionView[];
  seatById: Map<string, GameRecordPlayerView>;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  const numbers = round.type === 'numbers';

  return (
    <table className="w-full table-fixed border-collapse text-left">
      <caption className="sr-only">
        Round {round.index + 1}, {roundLabel(round.type)}: what each player answered.
      </caption>
      <thead>
        <tr className="border-b border-line">
          <th
            scope="col"
            className="label-mono py-0.5 pr-2"
            style={{ width: numbers ? PLAYER_COLUMN_NUMBERS : PLAYER_COLUMN_WORDS }}
          >
            player
          </th>
          <th scope="col" className="label-mono py-0.5 pr-2">
            {numbers ? 'answer' : 'word'}
          </th>
          <th
            scope="col"
            className="label-mono py-0.5 text-right"
            style={{ width: SCORE_COLUMN_PX }}
          >
            pts
          </th>
        </tr>
      </thead>

      <tbody>
        {answers.length === 0 ? (
          <tr>
            <td colSpan={3} className="py-2 text-[13px] text-ink-dim">
              Nobody answered this round.
            </td>
          </tr>
        ) : (
          answers.map((answer) => (
            <AnswerRow
              key={answer.playerId}
              record={record}
              round={round}
              answer={answer}
              seat={seatById.get(answer.playerId)}
              picked={picked}
              onPick={onPick}
            />
          ))
        )}
      </tbody>

      {round.best ? <BestRow round={round} best={round.best} picked={picked} onPick={onPick} /> : null}
    </table>
  );
}

function AnswerRow({
  record,
  round,
  answer,
  seat,
  picked,
  onPick,
}: {
  record: GameRecordResponse;
  round: GameRecordRoundView;
  answer: GameRecordSubmissionView;
  seat: GameRecordPlayerView | undefined;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  // A seat is always found: the endpoint drops any answer whose player has none.
  if (seat === undefined) return null;

  const score = answer.score ?? 0;

  return (
    <tr className="border-b border-line/50">
      {/* The player is the row's header, which is what makes a screen reader
          announce the name again beside the word and the score rather than
          reading out three unattributed cells. */}
      <th scope="row" className="py-1 pr-2 text-left align-middle font-normal">
        <PlayerChip
          player={seatAsPlayer(seat)}
          color={colorOf(seat, record.teams)}
          size="sm"
          // The standing at the top of the page is where a reader looks up who
          // somebody is. Forty repeats of the handle slot inside the rounds were
          // taking the width the word needed.
          showIdentity={false}
        />
      </th>
      <td className="py-1 pr-2 align-middle">
        {round.type === 'numbers' ? (
          <NumbersCell round={round} answer={answer} />
        ) : (
          <WordCell round={round} answer={answer} picked={picked} onPick={onPick} />
        )}
      </td>
      <td
        className={`tabular py-1 text-right align-middle font-mono text-[15px] font-bold ${
          score === 0 ? 'text-ink-dim' : 'text-ink'
        }`}
      >
        {score}
      </td>
    </tr>
  );
}

// --- A round too big to list -------------------------------------------------

/**
 * The column widths for the aggregated table.
 *
 * The answer leads here, which is the inversion the whole thing turns on — the
 * answer is the subject and the players are the detail — and it is why the two
 * layouts do not share `PLAYER_COLUMN_WORDS`. 40% of a 375px phone is 150px,
 * which holds nine mono characters at 15px with the multiplier beside them; the
 * names take what is left and truncate, because a name truncating is a smaller
 * loss than a word truncating and the whole page is built on that order.
 */
const ANSWER_COLUMN = '40%';

/**
 * A round drawn as a distribution: one row per ANSWER, with the room that found
 * it gathered behind it.
 *
 * Above `LARGE_ROOM_FROM` seats only — see `recordIsAggregated` — so an ordinary
 * game's record page is exactly what it was.
 *
 * It is still the same `<table>` as the ordinary round, with the same `<tfoot>`
 * and the same column count, for a reason that is not tidiness: fifteen rounds
 * alternating between a table and a list is a page that reads as two pages, and
 * the best-possible row has to stay in the column the answers are in either way.
 * What changes is what a row IS.
 *
 * Three rules are carried over from the live aggregated reveal rather than
 * re-decided, and each is argued there:
 *
 *   **Ranked by score, not by popularity.** The word forty people found is
 *   usually a short one.
 *
 *   **Every ruled-out answer is one row.** A rejected word per row is what makes
 *   a big round unreadable. They are still named, still struck through and still
 *   askable — they lose a row each and a score cell each that said `0` a dozen
 *   times over.
 *
 *   **Nothing outside the eight is dropped.** `otherAnswers`, `otherPlayers` and
 *   `noAnswer` are a line under the table, because eight of forty answers with
 *   nothing saying so is a silent cap.
 */
function AggregateTable({
  round,
  aggregate,
  seatById,
  picked,
  onPick,
}: {
  round: GameRecordRoundView;
  aggregate: RoundAggregate;
  seatById: Map<string, GameRecordPlayerView>;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  const numbers = round.type === 'numbers';
  const { stood, ruledOut } = splitByRuling(aggregate.top);
  const nameOf = (playerId: string) => seatById.get(playerId)?.displayName ?? null;

  const others = otherAnswersLine(aggregate);
  const nothing = noAnswerLine(aggregate);

  return (
    <div className="flex flex-col gap-1.5" data-aggregated="true">
      <table className="w-full table-fixed border-collapse text-left">
        <caption className="sr-only">
          Round {round.index + 1}, {roundLabel(round.type)}: the best answers, and who found
          each one.
        </caption>
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="label-mono py-0.5 pr-2" style={{ width: ANSWER_COLUMN }}>
              {numbers ? 'answer' : 'word'}
            </th>
            <th scope="col" className="label-mono py-0.5 pr-2">
              found by
            </th>
            <th
              scope="col"
              className="label-mono py-0.5 text-right"
              style={{ width: SCORE_COLUMN_PX }}
            >
              pts
            </th>
          </tr>
        </thead>

        <tbody>
          {stood.length === 0 && ruledOut.length === 0 ? (
            <tr>
              <td colSpan={3} className="py-2 text-[13px] text-ink-dim">
                Nobody answered this round.
              </td>
            </tr>
          ) : (
            <>
              {stood.map((entry) => (
                <AggregateRow
                  key={entry.answer}
                  numbers={numbers}
                  entry={entry}
                  finders={recordFindersLine(entry, nameOf)}
                  picked={picked}
                  onPick={onPick}
                />
              ))}
              <RuledOutRow numbers={numbers} rows={ruledOut} picked={picked} onPick={onPick} />
            </>
          )}
        </tbody>

        {round.best ? (
          <tfoot>
            <tr className="border-t border-blue-400/40">
              {/* The best answer takes the answer column, so it is read down the
                  same column as every answer above it — the same property the
                  ordinary table's `<tfoot>` was built for, with the columns the
                  other way round. */}
              <th scope="row" className="py-1.5 pr-2 text-left align-middle font-normal">
                <BestAnswerCell
                  round={round}
                  best={round.best}
                  picked={picked}
                  onPick={onPick}
                />
              </th>
              <td colSpan={2} className="py-1.5 align-middle">
                <BestLabel best={round.best} />
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>

      {/* Everything the eight rows do not account for. Under the table rather
          than in it, because these are not answers — they are the rest of the
          room, and a row for them among the words would read as one. */}
      {others === null && nothing === null ? null : (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px] text-ink-dim">
          {others === null ? null : <span data-other-answers="">{others}</span>}
          {nothing === null ? null : <span data-no-answer="">{nothing}</span>}
        </p>
      )}
    </div>
  );
}

/** One answer, and the room behind it. */
function AggregateRow({
  numbers,
  entry,
  finders,
  picked,
  onPick,
}: {
  numbers: boolean;
  entry: AggregateEntry;
  finders: string;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  const answer = entry.answer.toUpperCase();
  const score = entry.score;

  return (
    <tr className="border-b border-line/50" data-answer={answer} data-count={entry.count}>
      {/* The ANSWER is the row's header here, which is the inversion said in
          markup as well as in layout: a screen reader announces the word and
          then who found it, rather than a name and then a word. */}
      <th scope="row" className="py-1 pr-2 text-left align-middle font-normal">
        <span className="flex flex-wrap items-baseline gap-x-1.5">
          <AggregateWord
            numbers={numbers}
            answer={answer}
            out={false}
            picked={picked}
            onPick={onPick}
          />
          {/* How many found it, as a number. The record has no room for a bar
              per row and the count is the whole of what one would say. */}
          <span className="tabular font-mono text-[11px] text-ink-dim">&times;{entry.count}</span>
        </span>
      </th>
      <td className="truncate py-1 pr-2 align-middle text-[13px] text-ink-dim">{finders}</td>
      <td
        className={`tabular py-1 text-right align-middle font-mono text-[15px] font-bold ${
          score === 0 ? 'text-ink-dim' : 'text-ink'
        }`}
      >
        {score}
      </td>
    </tr>
  );
}

/**
 * Every ruled-out answer in the eight, in one row.
 *
 * The count leads, because that is the fact the reader is owed — words WERE
 * ruled out and here is how many — and the words follow as a wrapping list. They
 * wrap rather than truncate: a rejected word its author cannot read is the one
 * thing this page has never been allowed to do.
 *
 * No `pts` cell. Every row in it scored zero and a column of zeroes says nothing
 * a reader did not already get from the strike.
 */
function RuledOutRow({
  numbers,
  rows,
  picked,
  onPick,
}: {
  numbers: boolean;
  rows: AggregateEntry[];
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  if (rows.length === 0) return null;
  const players = playersBehind(rows);

  return (
    <tr className="border-b border-line/50" data-ruled-out={rows.length}>
      <th scope="row" className="py-1 pr-2 text-left align-middle font-normal">
        <span className="flex flex-wrap items-center gap-x-1.5">
          <RoundChip tone="alert">
            {rows.length === 1 ? '1 ruled out' : `${rows.length} ruled out`}
          </RoundChip>
          <span className="label-mono">{players === 1 ? '1 player' : `${players} players`}</span>
        </span>
      </th>
      <td colSpan={2} className="py-1 align-middle">
        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
          {rows.map((entry) => (
            <AggregateWord
              key={entry.answer}
              numbers={numbers}
              answer={entry.answer.toUpperCase()}
              out
              picked={picked}
              onPick={onPick}
            />
          ))}
        </span>
      </td>
    </tr>
  );
}

/**
 * One answer in an aggregated round, askable unless it is arithmetic.
 *
 * A numbers answer is an expression, and asking what `(75 - 3) * 7` means is a
 * control with one outcome — so it is a plain span there and a
 * `DefinitionWord` everywhere else. The strike is `line-through` on the word's
 * own text run and it must stay the only text decoration in here: CLAUDE.md, and
 * the first cut of the askable mark silently removed the strikethrough from every
 * ruled-out word on this page by reaching for `underline`.
 */
function AggregateWord({
  numbers,
  answer,
  out,
  picked,
  onPick,
}: {
  numbers: boolean;
  answer: string;
  out: boolean;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  const className = `font-mono text-[15px] font-bold tracking-[0.06em] ${
    out ? 'line-through decoration-alert decoration-2' : ''
  }`;

  if (numbers) return <span className={className}>{answer}</span>;
  return (
    <DefinitionWord
      word={answer}
      picked={picked === answer}
      onPick={(anchor) => onPick(answer, anchor)}
      className={className}
    >
      {answer}
    </DefinitionWord>
  );
}

/**
 * Why a word did not stand, in three words at most, beside the word itself.
 *
 * A host ruling is neutral because it is not a claim about the word. The other
 * two are the alert wash, which is the same one `RulingBar` wears — one wash over
 * an opaque surface, never nested inside another.
 */
function ReasonChip({ kind }: { kind: RuledOutKind }) {
  return (
    <RoundChip tone={kind === 'host-ruled' ? 'dim' : 'alert'} reason={kind}>
      {RULED_OUT_LABEL[kind]}
    </RoundChip>
  );
}

/**
 * A letters or conundrum answer.
 *
 * The word is text rather than a tile row. On the reveal a word is the thing on
 * the stage and it gets the tile face; here there are forty of them on one page
 * and a 46px tile row per answer was two thirds of the height of the page. The
 * mono face keeps it legible as a sequence of letters, which is what a tile row
 * was for.
 *
 * **Nothing is drawn over the word.** A rejected word is struck through by the
 * text run that draws it and named by a chip beside it, so the word stays fully
 * readable — the same rule the reveal keeps, for the same reason.
 *
 * **The word is also the control that asks what it means**, and it keeps every
 * one of the faces above while being one: the button wraps the same text run,
 * so the strikethrough is still drawn by the glyphs it crosses. A word that was
 * ruled out is still askable — `not a word` is the round's verdict against this
 * game's list, and whether a dictionary somewhere has heard of it is exactly the
 * question somebody asks next.
 */
function WordCell({
  round,
  answer,
  picked,
  onPick,
}: {
  round: GameRecordRoundView;
  answer: GameRecordSubmissionView;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  const word = answer.answer ?? '';
  if (word.length === 0) return <span className="text-[13px] text-ink-dim">nothing</span>;

  const out = rejected(answer);
  const reason = reasonOf(round, answer);
  // Only when it disagrees with what was played. Otherwise it is the word's own
  // length, counted a second time.
  const claimed =
    answer.declaredLength !== null && answer.declaredLength !== word.length
      ? answer.declaredLength
      : null;

  return (
    // The chip drops to a second line on a phone when the word is long — eight
    // letters and `not on the board` is 177px against the 145 a 375px phone
    // leaves this column — and that is the right way round: the word may never
    // be truncated or shrunk to make room for the reason it was struck out.
    <span className="flex flex-wrap items-center gap-x-1.5">
      <DefinitionWord
        word={word.toUpperCase()}
        picked={picked === word.toUpperCase()}
        onPick={(anchor) => onPick(word.toUpperCase(), anchor)}
        className={`font-mono text-[15px] font-bold tracking-[0.06em] ${
          out ? 'line-through decoration-alert decoration-2' : ''
        }`}
      >
        {word.toUpperCase()}
      </DefinitionWord>
      {reason === null ? null : <ReasonChip kind={reason} />}
      {claimed === null ? null : <span className="label-mono">claimed {claimed}</span>}
    </span>
  );
}

/**
 * One step of numbers working, tight enough for a table cell.
 *
 * `stepText` is the one implementation of resolving an operand into the number
 * it stood for, and it spaces its operator for a chip that has room. A cell does
 * not, and `100+10` is no less readable in a mono face than `100 + 10`.
 */
function compactStep(step: NumbersStep, tiles: number[], steps: NumbersStep[]): string {
  return `${stepText(step, tiles, steps).replace(/\s+/g, '')}=${step.result}`;
}

/**
 * A numbers answer: what they got, how far out it was, and how they got there.
 *
 * The working stays on the page in full. It was a wrapped strip of bordered
 * chips, 36px a step, which is what made a numbers round 2,976px; it is now a
 * line of small mono text under the value, and each step is `whitespace-nowrap`
 * so a step never breaks across two lines.
 */
function NumbersCell({
  round,
  answer,
}: {
  round: GameRecordRoundView;
  answer: GameRecordSubmissionView;
}) {
  if (answer.value === null) return <span className="text-[13px] text-ink-dim">nothing</span>;

  const steps = answer.steps ?? [];
  const tiles = round.numbersTiles ?? [];

  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="tabular font-mono text-[15px] font-bold">{answer.value}</span>
        <span className="label-mono">{numbersBand(round, answer)}</span>
      </span>
      {steps.length === 0 ? (
        <span className="text-[11px] text-ink-dim">no working</span>
      ) : (
        <span className="flex flex-wrap gap-x-2 font-mono text-[10px] leading-[14px] text-ink-dim">
          {steps.map((step) => (
            <span key={step.id} className="whitespace-nowrap">
              {compactStep(step, tiles, steps)}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

/** How far out a numbers answer was, which is what the show says about one. */
function numbersBand(round: GameRecordRoundView, answer: GameRecordSubmissionView): string {
  if (answer.value === null) return 'no answer';
  if (round.numbersTarget === null) return rejected(answer) ? 'not allowed' : 'allowed';
  const away = Math.abs(answer.value - round.numbersTarget);
  return away === 0 ? 'spot on' : `${away} away`;
}

/**
 * What was there, as the table's own last row.
 *
 * It was a blue frame under the answers — 135px for a letters round and 247 for
 * a numbers one — and a `<tfoot>` is both shorter and better placed: the best
 * word lands in the same column as every word played, so what the round was
 * worth is read down one column rather than across two layouts.
 *
 * The score column is left empty rather than filled with the length or with what
 * the word would have scored. Neither is stored, and a number under a heading
 * that says `pts` had better be points somebody got.
 */
function bestLabel(best: BestAnswer): string {
  return best.kind === 'letters'
    ? 'best possible'
    : best.kind === 'numbers'
      ? best.exact
        ? 'the target was there'
        : 'the closest there was'
      : 'the conundrum was';
}

/** The blue caption, in whichever cell the layout puts it. */
function BestLabel({ best }: { best: BestAnswer }) {
  return (
    <span className="font-mono text-[10px] font-semibold tracking-[0.12em] text-blue-200 uppercase">
      {bestLabel(best)}
    </span>
  );
}

function BestRow({
  round,
  best,
  picked,
  onPick,
}: {
  round: GameRecordRoundView;
  best: BestAnswer;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  return (
    <tfoot>
      <tr className="border-t border-blue-400/40">
        <th scope="row" className="py-1.5 pr-2 text-left align-middle font-normal">
          <BestLabel best={best} />
        </th>
        {/* Spanning the empty score column, so the alternatives get the width
            rather than a blank cell getting it. */}
        <td colSpan={2} className="py-1.5 align-middle">
          <BestAnswerCell round={round} best={best} picked={picked} onPick={onPick} />
        </td>
      </tr>
    </tfoot>
  );
}

/**
 * The best answer itself, without the row around it.
 *
 * Its own component because the two layouts put it in different cells: the
 * ordinary table leads with the caption and the aggregated one leads with the
 * word, so the word still lands in the column every other word on the page is
 * in. Everything about how it is drawn — the alternatives being controls, the
 * numbers working — is decided here once.
 */
function BestAnswerCell({
  round,
  best,
  picked,
  onPick,
}: {
  round: GameRecordRoundView;
  best: BestAnswer;
  picked: string | null;
  onPick: (word: string, anchor: HTMLElement) => void;
}) {
  return (
    <>
      {best.kind === 'numbers' ? (
            <span className="flex flex-col gap-0.5">
              <span className="tabular font-mono text-[15px] font-bold text-blue-200">
                {best.value}
              </span>
              {best.steps.length === 0 ? null : (
                <span className="flex flex-wrap gap-x-2 font-mono text-[10px] leading-[14px] text-ink-dim">
                  {best.steps.map((step) => (
                    <span key={step.id} className="whitespace-nowrap">
                      {compactStep(step, round.numbersTiles ?? [], best.steps)}
                    </span>
                  ))}
                </span>
              )}
            </span>
          ) : (
            <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <DefinitionWord
                word={best.word}
                picked={picked === best.word}
                onPick={(anchor) => onPick(best.word, anchor)}
                className="font-mono text-[15px] font-bold tracking-[0.06em] text-blue-200"
              >
                {best.word}
              </DefinitionWord>
              {/* The alternatives are askable too — the endpoint admits them
                  with the best word itself — so the run of names is built out
                  of controls rather than out of `listNames`. The grammar it
                  produced is kept by hand: commas, and `and` before the last. */}
              {best.kind === 'letters' && best.alternatives.length > 0 ? (
                <span className="text-[11px] text-ink-dim">
                  also{' '}
                  {best.alternatives.slice(0, 3).map((alternative, index, shown) => (
                    <span key={alternative}>
                      <DefinitionWord
                        word={alternative}
                        picked={picked === alternative}
                        onPick={(anchor) => onPick(alternative, anchor)}
                      >
                        {alternative}
                      </DefinitionWord>
                      {index === shown.length - 1 ? '' : index === shown.length - 2 ? ' and ' : ', '}
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
          )}
    </>
  );
}
